import { BrightcoveVideo } from "@blocks/BrightcoveVideo/BrightcoveVideo";
import CASBlock from "@blocks/CASBlock/CASBlock";
import RelatedArticleAccordion from "@components/Layouts/Accordion/RelatedArticleAccordion";
import ArticleImage from "@components/Layouts/Carousel/ArticleImage";
import RelatedArticleCarousel from "@components/Layouts/Carousel/RelatedArticleCarousel";
import { AdsImu1, AdsMidArticle } from "@elements/Advertisement/variant/AdsImu";
import {
  CompanionAdsImu1,
  CompanionAdsMidArticle,
} from "@elements/Advertisement/variant/CompanionAdImu";
import ApplyParagraphAnnotations from "@elements/ArticleParagraph/ApplyParagraphAnnotations";
import Paywall from "@elements/ArticleParagraph/ArticlePaywall";
import Regwall, {
  ArticleGaEventsDataProps,
} from "@elements/ArticleParagraph/ArticleRegwall";
import { YOUTUBE_WATCH_URL } from "@elements/ArticleParagraph/Embeds/constants";
import Embed from "@elements/ArticleParagraph/Embeds/Embed";
import YouTubeEmbedCustom from "@elements/ArticleParagraph/Embeds/YouTubeEmbedCustom";
import GetLimitedParagraphsFromArticleBody from "@elements/ArticleParagraph/GetLimitedParagraphsFromArticleBody";
import ParagraphList from "@elements/ArticleParagraph/ParagraphElements/ParagraphList";
import PullQuoteCard from "@elements/ArticleParagraph/ParagraphElements/PullQuoteCard";
import SubHead from "@elements/ArticleParagraph/ParagraphElements/SubHead";
import WrapperContainer from "@elements/ArticleParagraph/ParagraphElements/WrapperContainer";
import Paragraph from "@elements/Typography/Paragraph";
import debounce from "@helper/debounce";
import {
  CAS_CAMPAIGN_ID,
  SHOW_PAYWALL,
  SHOW_REGWALL,
} from "@helper/getEnvVariables";
import setArticleVisitCookie, {
  SphUserArticleCountCookie,
} from "@helper/setArticleVisitCookie";
import { ProcessedArticleData } from "@helper/transformer/useOsResponse";
import { formatDate } from "@helper/useDateFormat";
import {
  getWindowOrigin,
  isArrayWithElements,
  isClientSide,
} from "@helper/utils";
import { isElementInViewPort } from "@helper/visibilityHelpers";
import { useTMCustomEventAnalyticsCall } from "@hooks/useAnalyticsCall/useTMCustomEventAnalyticsCall";
import useCasScript from "@hooks/useCasScript";
import { getUserType, OKTAUserTypeEnum } from "@hooks/useUserInfo";
import { useWindowSize } from "@hooks/useWindowSize/useWindowSize";
import {
  ArticleAdPageTargetingType,
  ArticleAdSlotTargetingType,
  PageAdTargetingTypeEnum,
} from "@typings/Ads.d";
import {
  type ProcessedArticleBody,
  ProcessedAuthorContent,
  ProcessedTagsContent,
} from "@typings/Opensearch";
import Cookies from "js-cookie";
import { Fragment, useCallback, useEffect, useState } from "react";

export type ArticleParagraphProps = {
  articleId: string;
  articleTitle: string;
  articleBody: ProcessedArticleBody;
  sectionName: string | undefined;
  tags?: ProcessedTagsContent[];
  pageUrl: string;
  pageNumber: number;
  dateCreated: string;
  isInfiniteScrollArticle?: boolean;
  authors?: ProcessedAuthorContent[];
  articlePageTarget?: ArticleAdPageTargetingType[];
  articleSlotTarget?: ArticleAdSlotTargetingType[];
  gsChannels?: string[];
  endnote?: string;
  isCuePreview?: boolean;
  relatedStoriesData?: ProcessedArticleData[];
  bodyParagraphTotal?: number;
};

export default function ArticleParagraph({
  articleId,
  articleTitle,
  articleBody,
  sectionName,
  tags,
  pageUrl,
  pageNumber,
  dateCreated,
  isInfiniteScrollArticle,
  authors,
  articlePageTarget,
  articleSlotTarget,
  gsChannels,
  endnote,
  isCuePreview = false,
  relatedStoriesData,
  bodyParagraphTotal = 0,
}: ArticleParagraphProps) {
  const origin = getWindowOrigin();
  const [articleParagraphs, setArticleParagraphs] =
    useState<ProcessedArticleBody | null>(articleBody);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const tmCustomGaEventsProps = {
    articleid: articleId,
    contenttype: "1",
    chapter1: "",
    level2: sectionName,
    pubdate: formatDate(dateCreated, "yyyy-MM-dd HH:mm:ss"),
    title: articleTitle,
    url: origin + pageUrl,
    keyword: tags ? tags.map((tags) => tags.name).join(", ") : "",
    byline: authors ? authors.map((author) => author.name).join(", ") : "",
  } as ArticleGaEventsDataProps;

  const TmCustomGaEvent = useTMCustomEventAnalyticsCall();
  const [enableRegwall, setEnableRegwall] = useState<boolean>(false);
  const [enablePaywall, setEnablePaywall] = useState<boolean>(false);
  const [cookieData, setCookieData] = useState<SphUserArticleCountCookie>();
  const { isScriptReady: isCasScriptReady } = useCasScript();

  const handleScroll = useCallback(() => {
    if (!isClientSide) return;
    const articleBody = document.querySelectorAll(
      `.article-body-wrapper-${articleId}`,
    );
    articleBody.forEach((article) => {
      if (isElementInViewPort(article, 0)) {
        // Set user page visit count and ids.
        setArticleVisitCookie({ articleId });

        // Retrieve the existing cookie
        const cookieName = "sph_user_article_count";
        const existingCookie = Cookies.get(cookieName);
        if (existingCookie) {
          const cookieDataParsed = JSON.parse(
            existingCookie,
          ) as SphUserArticleCountCookie;
          setCookieData(cookieDataParsed);
        }
      }
    });
  }, [articleId]);

  useEffect(() => {
    if (!isClientSide) return;
    const debouncedHandleScroll = debounce(handleScroll, 100);
    window.addEventListener("scroll", debouncedHandleScroll);
    return () => {
      window.removeEventListener("scroll", debouncedHandleScroll);
    };
  }, [handleScroll]);

  useEffect(() => {
    const userType = getUserType();

    let visitCount = 0;
    let UserArticleVisitNumber = visitCount;
    if (cookieData) {
      visitCount = cookieData.count;
      UserArticleVisitNumber = visitCount;
      // Get the visit count
      if (cookieData.nids.includes(articleId)) {
        UserArticleVisitNumber = cookieData.nids.indexOf(articleId) + 1;
      }
    }
    if (
      !isCuePreview &&
      parseInt(SHOW_REGWALL) &&
      visitCount >= 5 &&
      UserArticleVisitNumber >= 5 &&
      (userType === OKTAUserTypeEnum.ANONYMOUS || userType === undefined)
    ) {
      setEnableRegwall(true);
    }
    if (
      !isCuePreview &&
      parseInt(SHOW_REGWALL) &&
      parseInt(SHOW_PAYWALL) &&
      visitCount >= 7 &&
      UserArticleVisitNumber >= 7 &&
      userType !== OKTAUserTypeEnum.SUBSCRIBER &&
      userType === OKTAUserTypeEnum.REGISTERED
    ) {
      setEnableRegwall(false);
      setEnablePaywall(true);
    }
  }, [articleId, cookieData, isCuePreview]);

  useEffect(() => {
    if (enableRegwall || enablePaywall) {
      setArticleParagraphs(GetLimitedParagraphsFromArticleBody(articleBody, 1));
    } else {
      setArticleParagraphs(articleBody);
    }

    const cookieName = "sph_user_article_count";
    const existingCookie = Cookies.get(cookieName);
    if (enableRegwall) {
      // Set GA events.
      TmCustomGaEvent({
        event: "customEvent",
        eventCategory: "Article Hard Regwall",
        eventAction: "Visible",
        abVariant: "hard regwall",
        eventLabel: existingCookie,
        ...tmCustomGaEventsProps,
      });
    }
    if (enablePaywall) {
      // Set GA events.
      TmCustomGaEvent({
        event: "customEvent",
        eventCategory: "Article Paywall",
        eventAction: "Visible",
        abVariant: "article paywall",
        eventLabel: existingCookie,
        ...tmCustomGaEventsProps,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [articleBody, enableRegwall, enablePaywall]);

  let paragraphCount = 0;
  const pageAdTarget = PageAdTargetingTypeEnum.ARTICLE;

  let adsImu1 = (
    <div className="border-y border-solid border-gray-2200 mb-5">
      <AdsImu1
        pageAdTargetType={pageAdTarget}
        sectionName={sectionName}
        rootClassName={"pt-5"}
        gsChannels={gsChannels}
        articlePageTarget={articlePageTarget}
      />
    </div>
  );
  let adsMidArticle = (
    <div className="border-y border-solid border-gray-2200 mb-5">
      <AdsMidArticle
        pageAdTargetType={pageAdTarget}
        sectionName={sectionName}
        rootClassName={"py-5"}
        gsChannels={gsChannels}
        articlePageTarget={articlePageTarget}
      />
    </div>
  );

  if (isInfiniteScrollArticle) {
    adsImu1 = (
      <div className="border-y border-solid border-gray-2200 mb-5">
        <CompanionAdsImu1
          uniqueSectionName={sectionName}
          pageAdTargetType={pageAdTarget}
          pageNumber={pageNumber}
          articlePageTarget={articlePageTarget}
          articleSlotTarget={articleSlotTarget}
          rootClassName={"pt-5"}
          gsChannels={gsChannels}
        />
      </div>
    );
    adsMidArticle = (
      <div className="border-y border-solid border-gray-2200 mb-5">
        <CompanionAdsMidArticle
          uniqueSectionName={sectionName}
          pageAdTargetType={pageAdTarget}
          pageNumber={pageNumber}
          articlePageTarget={articlePageTarget}
          articleSlotTarget={articleSlotTarget}
          rootClassName={"py-5"}
          gsChannels={gsChannels}
        />
      </div>
    );
  }
  const { isScreenLG } = useWindowSize();

  return (
    <div
      data-testid="article-body"
      className={`article-body-wrapper-${articleId}`}
    >
      {articleParagraphs &&
        articleParagraphs.map((element, index) => {
          let bannersAndAds = null;
          let relatedArticles = null;
          if (paragraphCount === 0) {
            if (enableRegwall) {
              bannersAndAds = (
                <>
                  <Regwall articleData={tmCustomGaEventsProps} />
                  {adsImu1}
                </>
              );
            } else if (enablePaywall) {
              bannersAndAds = (
                <>
                  <Paywall articleData={tmCustomGaEventsProps} />
                  {adsImu1}
                </>
              );
            } else {
              bannersAndAds = <>{adsImu1}</>;
            }
          } else if (paragraphCount === 5) {
            bannersAndAds = <>{adsMidArticle}</>;
          } else if (
            bodyParagraphTotal >= 9 &&
            paragraphCount === 7 &&
            relatedStoriesData &&
            isArrayWithElements(relatedStoriesData)
          ) {
            relatedArticles = isScreenLG ? (
              <RelatedArticleCarousel
                relatedArticles={relatedStoriesData}
                rootClassName="track-impression hidden lg:block mt-6 overflow-hidden bg-gray-500 p-5 mb-5"
                testId="related-article-carousel"
                dataSectionName="related-story-middle"
                source="related-center"
                middleArticle={true}
              />
            ) : (
              <RelatedArticleAccordion
                relatedArticles={relatedStoriesData}
                rootClassName="track-impression grid lg:hidden mt-6 overflow-hidden bg-gray-500 p-2.5 mb-5 gap-2.5"
                testId="related-article-accordion"
                source="related-center"
                dataSectionName="related-story-middle"
                middleArticle={true}
              />
            );
          }

          switch (element.type) {
            case "paragraph":
              paragraphCount++;
              return (
                <Fragment key={index}>
                  <Paragraph
                    testid={`paragraph-${index}`}
                    id={element.id}
                    className={paragraphCount == 1 ? "article-summary" : ""}
                  >
                    {ApplyParagraphAnnotations(
                      element.value,
                      element.annotations,
                    )}
                  </Paragraph>
                  {bannersAndAds}
                  {relatedArticles}
                </Fragment>
              );

            case "sub_head":
              return (
                <Fragment key={index}>
                  <SubHead element={element} />
                </Fragment>
              );

            case "image": {
              const imageHeight =
                element.image?.height && element.image.height >= 1460
                  ? 1460
                  : 550;
              return (
                <ArticleImage
                  key={index}
                  url={element.image?.url}
                  alt={element.caption}
                  caption={element.caption}
                  credit={element.credit}
                  height={imageHeight}
                  className="mb-10 mt-2"
                />
              );
            }

            case "embed":
              return (
                <Fragment key={index}>
                  <Embed className="my-lg" src={element.embedUrl} />
                </Fragment>
              );

            case "pull_quote":
              return (
                <Fragment key={index}>
                  <PullQuoteCard
                    quote={element.quote}
                    quotee={element.quotee}
                    quoteLabel={element.quoteLabel}
                    quoteCredit={element.quoteCredit}
                  />
                </Fragment>
              );

            case "list_bulleted":
            case "list_numbered": {
              const listType = element.type === "list_bulleted" ? "ol" : "ul";
              const listItems = element.listItem;
              const listStyle =
                element.listStyle === "decimal" ? "list-decimal" : "list-disc";
              return (
                <Fragment key={index}>
                  <ParagraphList
                    listStyle={listStyle}
                    listType={listType}
                    listItems={listItems}
                  />
                </Fragment>
              );
            }

            case "youtube_video": {
              const url = `${YOUTUBE_WATCH_URL}${element.youtubeId}`;
              return (
                <Fragment key={index}>
                  <YouTubeEmbedCustom url={url} className="my-lg" />
                </Fragment>
              );
            }

            case "brightcove_video": {
              return (
                <Fragment key={index}>
                  <WrapperContainer className="block my-lg">
                    <BrightcoveVideo videoId={element.videoId} />
                  </WrapperContainer>
                </Fragment>
              );
            }

            default:
              return null;
          }
        })}

      {endnote && (
        <div className="bg-gray-600 px-2.5 py-0.5 mb-4">{endnote}</div>
      )}

      {isCasScriptReady && (
        <CASBlock casId={CAS_CAMPAIGN_ID} index={pageNumber} />
      )}
    </div>
  );
}
